import { useTranslation } from 'react-i18next';

import { WidgetSlot, WidgetType } from '@/api/rest/resources/types/widget';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';

import { useOrganizationPhysicalRisk } from '../hooks/useOrganizationPhysicalRisk';

const X_AXIS_PROPS = { dataKey: 'label', type: 'category' as const, interval: 4, paddingLeft: 40 };

const C = {
  hazard: '#C4A465',
  exposure: '#4FADA8',
  vulnerability: '#5FA85A',
  eiiBaseline: '#9E9E9E',
  eiiRecommended: '#4FADA8',
  eiiHighAmbition: '#2E7D32',
} as const;

const C_DEP = {
  vulnerability: '#2E7D32',
  physical_stress: '#E07A5F',
  interaction: '#C4A465',
} as const;

const NRP_NARRATIVE: Record<string, string> = {
  vulnerability: 'Benefits from vulnerability reduction or EII gains more than hazard reduction',
  physical_stress: 'Benefits from exposure reduction more than vulnerability reduction or EII gains',
  interaction: 'Benefits equally from addressing hazard and vulnerability together',
};

const BIOPHYSICAL_RISK_INDICATORS = [
  {
    name: 'hazard',
    title: 'Hazard',
    tooltip: 'Possibility of external unmanaged climate hazards',
  },
  { name: 'exposure', title: 'Exposure', tooltip: "Site's spatial/operational exposure" },
  {
    name: 'vulnerability',
    title: 'Vulnerability',
    tooltip: 'Sensitivity to nature degradation (1 - Ecosystem Integrity Index)',
  },
] as const;

export const ScenarioBiophysicalDetails = () => {
  const { t } = useTranslation();
  const { data } = useOrganizationPhysicalRisk();

  const trajectoryData = data.baseline.map((row) => ({
    label: `${row.calendar_year}`,
    hazard: row.hazard,
    exposure: row.exposure,
    vulnerability: row.vulnerability,
  }));

  const eiiData = data.baseline.map((row, i) => ({
    label: `${row.calendar_year}`,
    baseline: row.eii,
    recommended: data.recommended[`${i}`]?.eii ?? row.eii,
    highAmbition: data.high_ambition[`${i}`]?.eii ?? row.eii,
  }));

  const baselineY0 = data.baseline[0];
  const kpiValues: Record<string, number> = {
    hazard: baselineY0?.hazard ?? 0,
    exposure: baselineY0?.exposure ?? 0,
    vulnerability: baselineY0?.vulnerability ?? 0,
  };

  const HEADERS_CLASSNAME = 'flex flex-col gap-2';
  const HEADER_CLASSNAME = 'typography-overline text-text-primary font-semibold';
  const SUBHEADER_CLASSNAME = 'typography-body1 text-text-secondary';
  const SECTION_HEADER_CLASSNAME = 'typography-body1 text-text-primary';

  const dep = baselineY0?.dependency;
  const dependencyPieData = [
    { name: 'Vulnerability', value: dep?.vulnerability ?? 0, color: C_DEP.vulnerability },
    { name: 'Physical stress', value: dep?.physical_stress ?? 0, color: C_DEP.physical_stress },
    { name: 'Interaction', value: dep?.interaction ?? 0, color: C_DEP.interaction },
  ];
  const dominantKey = dep
    ? (['vulnerability', 'physical_stress', 'interaction'] as const).reduce((a, b) =>
        dep[`${a}`] > dep[`${b}`] ? a : b,
      )
    : 'vulnerability';
  const narrativeText = NRP_NARRATIVE[`${dominantKey}`];

  const baselineWidget = [
    {
      name: 'baseline_widgets',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: HEADERS_CLASSNAME },
      widgets: [
        {
          name: 'baseline_widget_headings',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: HEADERS_CLASSNAME },
          widgets: [
            {
              name: 'physical_risk_baseline_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: { text: t('shared.scenarioAnalysis.labels.measuredBaseline'), className: HEADER_CLASSNAME },
            },
            {
              name: 'physical_risk_baseline_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.measuredBaselineDescription'),
                className: SUBHEADER_CLASSNAME,
              },
            },
          ],
        },
        {
          name: 'physical_risk_baseline_section',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: 'flex flex-col gap-4' },
          widgets: [
            {
              name: 'physical_risk_baseline_cards',
              type: WidgetType.a_stack,
              visible: true,
              relatedData: { className: 'grid grid-cols-2 gap-3 items-start' },
              widgets: [
                {
                  name: 'physical_risk_indicator_tiles_col',
                  type: WidgetType.a_stack,
                  visible: true,
                  relatedData: { className: 'flex flex-col gap-3' },
                  widgets: BIOPHYSICAL_RISK_INDICATORS.flatMap((indicator): WidgetSlot[] => [
                    {
                      name: `physical_risk_${indicator.name}`,
                      type: WidgetType.o_card,
                      visible: true,
                      relatedData: {
                        styles: {
                          root: 'rounded-lg justify-center items-center w-full',
                          body: 'flex flex-col gap-4 p-3 justify-center items-center w-full',
                        },
                      },
                      widgets: [
                        {
                          name: `physical_risk_${indicator.name}_label`,
                          type: WidgetType.a_text,
                          visible: true,
                          relatedData: {
                            text: indicator.title,
                            className: SECTION_HEADER_CLASSNAME,
                          },
                        },
                        {
                          name: `physical_risk_${indicator.name}_header`,
                          type: WidgetType.a_text,
                          visible: true,
                          widgets: [
                            {
                              name: `physical_risk_${indicator.name}_value`,
                              type: WidgetType.a_text,
                              visible: true,
                              relatedData: {
                                text: kpiValues[indicator.name],
                                className: 'typography-h2 text-text-primary',
                              },
                            },
                            {
                              name: `physical_risk_${indicator.name}_margin`,
                              type: WidgetType.a_text,
                              visible: true,
                              relatedData: { text: ' / 100', className: 'typography-caption text-text-secondary' },
                            },
                          ],
                        },
                        {
                          name: `physical_risk_${indicator.name}_description`,
                          type: WidgetType.a_text,
                          visible: true,
                          relatedData: {
                            text: indicator.tooltip,
                            className: 'whitespace-pre-wrap typography-body2 text-text-secondary',
                          },
                        },
                      ],
                    },
                  ]),
                },
                {
                  name: 'physical_risk_nrp_composition_card',
                  type: WidgetType.o_card,
                  visible: true,
                  relatedData: {
                    styles: {
                      root: 'rounded-lg h-full justify-center items-center',
                      body: 'flex flex-col gap-2 p-3 h-full justify-between items-center',
                    },
                  },
                  widgets: [
                    {
                      name: 'physical_risk_nrp_composition_title',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: 'Dependency',
                        className: SECTION_HEADER_CLASSNAME,
                      },
                    },
                    {
                      name: 'physical_risk_nrp_pie_graph',
                      type: WidgetType.o_composite_graph,
                      visible: true,
                      relatedData: {
                        data: dependencyPieData,
                        chartConfig: [
                          {
                            shape: ChartShape.pie,
                            dataKey: 'value',
                            label: 'NRP composition',
                            color: C_DEP.vulnerability,
                            key: 'nrp_composition',
                            id: 'nrp_composition',
                            innerRadius: 55,
                            outerRadius: 75,
                            height: 220,
                          },
                        ],
                        legendItems: [
                          { label: 'Vulnerability', color: C_DEP.vulnerability },
                          { label: 'Physical stress', color: C_DEP.physical_stress },
                          { label: 'Interaction', color: C_DEP.interaction },
                        ],
                      },
                    },
                    {
                      name: 'physical_risk_nrp_narrative',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: narrativeText,
                        className: 'whitespace-normal px-6 typography-body2 text-text-secondary',
                      },
                    },
                  ],
                } satisfies WidgetSlot,
              ],
            },
          ],
        },
      ],
    },
  ];

  const baselineTrajectoryWidget = [
    {
      name: 'baseline_trajectory_widgets',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: { className: HEADERS_CLASSNAME },
      widgets: [
        {
          name: 'baseline_trajectory_headings',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: HEADERS_CLASSNAME },
          widgets: [
            {
              name: 'physical_risk_trajectory_section_header',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.scenarioTrajectory'),
                className: HEADER_CLASSNAME,
              },
            },
            {
              name: 'physical_risk_trajectory_section_description',
              type: WidgetType.a_text,
              visible: true,
              relatedData: {
                text: t('shared.scenarioAnalysis.labels.scenarioTrajectoryDescription'),
                className: SUBHEADER_CLASSNAME,
              },
            },
          ],
        },
        {
          name: 'physical_risk_biophysical_charts_row',
          type: WidgetType.a_stack,
          visible: true,
          relatedData: { className: 'grid grid-cols-2 gap-4' },
          widgets: [
            {
              name: 'physical_risk_trajectory_chart_card',
              type: WidgetType.o_card,
              visible: true,
              relatedData: {
                styles: {
                  root: 'flex-1 min-w-[280px] border border-neutral-black-12 rounded-lg overflow-hidden',
                  body: HEADERS_CLASSNAME,
                },
              },
              widgets: [
                {
                  name: 'physical_risk_trajectory_chart_card_headings',
                  type: WidgetType.a_stack,
                  visible: true,
                  relatedData: { className: 'flex flex-col gap-2 justify-center items-center' },
                  widgets: [
                    {
                      name: 'physical_risk_trajectory_section_header',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: 'Climate variables trajectory',
                        className: SECTION_HEADER_CLASSNAME,
                      },
                    },
                    {
                      name: 'physical_risk_trajectory_section_description',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: 'Starting from latest year to biophysical horizon (Settings affect Hazard and timeline only)',
                        className: 'typography-body2 text-text-secondary',
                      },
                    },
                  ],
                },
                {
                  name: 'physical_risk_trajectory_graph',
                  type: WidgetType.o_composite_graph,
                  visible: true,
                  relatedData: {
                    data: trajectoryData,
                    className: 'px-0.5 py-1 pr-[30px]',
                    chartConfig: BIOPHYSICAL_RISK_INDICATORS.map((indicator) => ({
                      shape: ChartShape.line,
                      dataKey: indicator.name,
                      label: indicator.title,
                      color: C[indicator.name],
                      key: indicator.name,
                      id: indicator.name,
                      dot: false,
                      strokeWidth: 2,
                    })),
                    xAxisProps: X_AXIS_PROPS,
                    yAxisProps: {
                      label: 'Score',
                      domain: ['dataMin', 'dataMax'],
                    },
                    legendItems: BIOPHYSICAL_RISK_INDICATORS.map((indicator) => ({
                      label: indicator.title,
                      color: C[indicator.name],
                    })),
                  },
                },
              ],
            },

            {
              name: 'physical_risk_eii_chart_card',
              type: WidgetType.o_card,
              visible: true,
              relatedData: {
                styles: {
                  root: 'flex-1 min-w-[280px] border border-neutral-black-12 rounded-lg overflow-hidden',
                  body: HEADERS_CLASSNAME,
                },
              },
              widgets: [
                {
                  name: 'physical_risk_eii_chart_card_headings',
                  type: WidgetType.a_stack,
                  visible: true,
                  relatedData: { className: 'flex flex-col gap-2 justify-center items-center' },
                  widgets: [
                    {
                      name: 'physical_risk_eii_chart_card_header',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: 'Ecosystem Integrity Index (EII) trajectory',
                        className: SECTION_HEADER_CLASSNAME,
                      },
                    },
                    {
                      name: 'physical_risk_eii_chart_card_description',
                      type: WidgetType.a_text,
                      visible: true,
                      relatedData: {
                        text: 'Scaled to 0-100. Higher EII lowers NRP vulnerability (Settings only affect timeline)',
                        className: 'typography-body2 text-text-secondary',
                      },
                    },
                  ],
                },
                {
                  name: 'physical_risk_eii_graph',
                  type: WidgetType.o_composite_graph,
                  visible: true,
                  relatedData: {
                    data: eiiData,
                    className: 'px-0.5 py-1 pr-[30px]',
                    chartConfig: [
                      {
                        shape: ChartShape.line,
                        dataKey: 'baseline',
                        label: 'Baseline',
                        color: C.eiiBaseline,
                        key: 'baseline',
                        id: 'baseline',
                        dot: false,
                        strokeWidth: 2,
                        strokeDasharray: '5 5',
                      },
                      {
                        shape: ChartShape.line,
                        dataKey: 'recommended',
                        label: 'Recommended',
                        color: C.eiiRecommended,
                        key: 'recommended',
                        id: 'recommended',
                        dot: false,
                        strokeWidth: 2,
                      },
                      {
                        shape: ChartShape.line,
                        dataKey: 'highAmbition',
                        label: 'High ambition',
                        color: C.eiiHighAmbition,
                        key: 'highAmbition',
                        id: 'highAmbition',
                        dot: false,
                        strokeWidth: 2,
                      },
                    ],
                    xAxisProps: X_AXIS_PROPS,
                    yAxisProps: {
                      label: 'EII',
                      domain: ['dataMin', 'dataMax'],
                    },
                    legendItems: [
                      { label: 'Baseline (do nothing)', color: C.eiiBaseline },
                      { label: 'Recommended', color: C.eiiRecommended },
                      { label: 'High ambition', color: C.eiiHighAmbition },
                    ],
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  const defaultMethodologyConfiguration = useActiveWidgetSlotConfiguration(
    'default_scenario_modelling_methodology_inputs_tab',
    WidgetType.a_stack,
  );

  const summaryWidget = (defaultMethodologyConfiguration?.widgets as WidgetSlot[] | undefined)?.find(
    (w: WidgetSlot) => w.name === 'biophysical_risk_summary_stack',
  ) ?? {
    name: 'biophysical_risk_summary_stack',
    type: WidgetType.o_card,
    visible: false,
  };

  const defaultConfiguration: WidgetSlot = {
    name: 'physical_risk_tab',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: { className: 'flex flex-col gap-8' },
    widgets: [{ ...summaryWidget, visible: true }, ...baselineWidget, ...baselineTrajectoryWidget],
  };
  return <WidgetSlotRenderer nestedWidget={defaultConfiguration} />;
};
